import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

const STATE_COLORS = {
  ARMED: 0x00D7FF,
  LISTENING: 0x19F2D6,
  THINKING: 0xFFB020,
  SPEAKING: 0x00D7FF,
  STANDBY: 0x7C8AA6,
  BOOTING: 0xB9F3FF,
};

export default function HolographicScene({ state = 'ARMED' }) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const stateRef = useRef(state);

  useEffect(() => { stateRef.current = state; }, [state]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.set(0, 2, 8);
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    // Grid floor
    const gridHelper = new THREE.GridHelper(40, 60, 0x00D7FF, 0x00D7FF);
    gridHelper.position.y = -2.5;
    gridHelper.material.opacity = 0.06;
    gridHelper.material.transparent = true;
    scene.add(gridHelper);

    // Particles
    const particleCount = 150;
    const particleGeo = new THREE.BufferGeometry();
    const particlePos = new Float32Array(particleCount * 3);
    const particleVel = [];
    for (let i = 0; i < particleCount; i++) {
      particlePos[i * 3] = (Math.random() - 0.5) * 20;
      particlePos[i * 3 + 1] = Math.random() * 8 - 2;
      particlePos[i * 3 + 2] = (Math.random() - 0.5) * 20;
      particleVel.push({
        x: (Math.random() - 0.5) * 0.003,
        y: (Math.random() - 0.5) * 0.002,
        z: (Math.random() - 0.5) * 0.003,
      });
    }
    particleGeo.setAttribute('position', new THREE.BufferAttribute(particlePos, 3));
    const particleMat = new THREE.PointsMaterial({
      size: 0.04,
      color: 0x00D7FF,
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    const particles = new THREE.Points(particleGeo, particleMat);
    scene.add(particles);

    // Holographic rings — more of them, more varied
    function createRing(radius, yPos, opacity) {
      const geo = new THREE.TorusGeometry(radius, 0.008, 8, 128);
      const mat = new THREE.MeshBasicMaterial({
        color: 0x00D7FF,
        transparent: true,
        opacity: opacity,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.y = yPos;
      scene.add(mesh);
      return mesh;
    }

    const ring1 = createRing(3.5, 0, 0.15);
    const ring2 = createRing(2.8, 0.4, 0.10);
    const ring3 = createRing(4.5, -0.3, 0.07);
    const ring4 = createRing(2.0, 0.8, 0.05);
    const ring5 = createRing(5.5, -0.5, 0.04);
    const ring6 = createRing(1.5, -0.1, 0.08);

    // Core glow
    const coreGeo = new THREE.SphereGeometry(0.4, 16, 16);
    const coreMat = new THREE.MeshBasicMaterial({
      color: 0x00D7FF,
      transparent: true,
      opacity: 0.06,
      blending: THREE.AdditiveBlending,
    });
    const core = new THREE.Mesh(coreGeo, coreMat);
    core.position.y = 0.5;
    scene.add(core);

    // Vertical data lines
    const dataLines = [];
    for (let i = 0; i < 10; i++) {
      const lineGeo = new THREE.BoxGeometry(0.003, 1 + Math.random() * 2, 0.003);
      const lineMat = new THREE.MeshBasicMaterial({
        color: 0x19F2D6,
        transparent: true,
        opacity: 0.12,
        blending: THREE.AdditiveBlending,
      });
      const line = new THREE.Mesh(lineGeo, lineMat);
      line.position.set(
        (Math.random() - 0.5) * 14,
        Math.random() * 4 - 2,
        (Math.random() - 0.5) * 14
      );
      line.userData = { speed: 0.3 + Math.random() * 0.4, offset: Math.random() * 6 };
      scene.add(line);
      dataLines.push(line);
    }

    // Ambient light
    const ambient = new THREE.AmbientLight(0x00D7FF, 0.03);
    scene.add(ambient);

    // Animate
    const clock = new THREE.Clock();
    let animId;

    function animate() {
      animId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      const currentColor = STATE_COLORS[stateRef.current] || STATE_COLORS.ARMED;

      // Rotate rings — varied speeds for depth
      ring1.rotation.y = t * 0.2;
      ring1.rotation.x = Math.sin(t * 0.15) * 0.08;
      ring2.rotation.y = -t * 0.25;
      ring2.rotation.x = Math.sin(t * 0.12) * 0.06;
      ring3.rotation.y = t * 0.12;
      ring4.rotation.y = -t * 0.35;
      ring4.rotation.z = Math.sin(t * 0.1) * 0.15;
      ring5.rotation.y = t * 0.08;
      ring5.rotation.x = Math.cos(t * 0.1) * 0.05;
      ring6.rotation.y = t * 0.5;
      ring6.rotation.x = Math.sin(t * 0.2) * 0.2;

      // Update ring colors
      ring1.material.color.setHex(currentColor);
      ring2.material.color.setHex(currentColor);

      // Pulse core
      const isActive = stateRef.current === 'THINKING' || stateRef.current === 'SPEAKING' || stateRef.current === 'LISTENING';
      const pulseSpeed = isActive ? 3 : 1;
      const pulse = 0.8 + Math.sin(t * pulseSpeed) * 0.2;
      core.scale.setScalar(pulse);
      core.material.color.setHex(currentColor);
      core.material.opacity = isActive ? 0.1 : 0.06;

      // Move particles
      const posAttr = particles.geometry.attributes.position;
      for (let i = 0; i < particleCount; i++) {
        posAttr.array[i * 3] += particleVel[i].x;
        posAttr.array[i * 3 + 1] += particleVel[i].y;
        posAttr.array[i * 3 + 2] += particleVel[i].z;
        // Wrap around
        if (Math.abs(posAttr.array[i * 3]) > 10) particleVel[i].x *= -1;
        if (posAttr.array[i * 3 + 1] > 6 || posAttr.array[i * 3 + 1] < -2) particleVel[i].y *= -1;
        if (Math.abs(posAttr.array[i * 3 + 2]) > 10) particleVel[i].z *= -1;
      }
      posAttr.needsUpdate = true;

      // Animate data lines
      dataLines.forEach(line => {
        const lt = (t * line.userData.speed + line.userData.offset) % 5;
        line.position.y = lt - 2;
        line.material.opacity = lt < 4 ? 0.12 : 0.12 * (5 - lt);
      });

      // Grid pulse
      gridHelper.material.opacity = 0.05 + Math.sin(t * 0.3) * 0.015;

      renderer.render(scene, camera);
    }
    animate();

    // Resize handler
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    sceneRef.current = { scene, renderer, animId };

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
}
